import { inject } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import type { ResolveFn } from '@angular/router';
import { Router } from '@angular/router';
import { OnyxToastService } from '@onyx/angular';
import { catchError, map, of } from 'rxjs';
import type { PartialDeep } from 'type-fest';
import type { CompanyData } from '../../../../../common/interfaces/data-providers/company-data';
import { CacheService } from '../../../../../common/services/cache.service';
import { DataProvidersService } from '../../../../../common/services/data-providers.service';
import { PreferencesService } from '../../../../../common/services/preferences.service';
import { ContractorInvoiceDocumentsFormComponent } from '../../contractor-form/contractor-invoice-documents-form/contractor-invoice-documents-form.component';
import { ContractorInvoiceFormComponent } from '../../contractor-form/contractor-invoices-form/contractor-invoice-form/contractor-invoice-form.component';
import type { ContractorFormResolverData } from '../../contractors.routes';
import { ContractorsRouterStateKey } from '../enums/contractors-router-state-key';
import { ContractorsStorageKey } from '../enums/contractors-storage-key';
import type { ContractorForm } from '../helpers/contractor-form.helper';
import { ContractorFormHelper } from '../helpers/contractor-form.helper';

export const addContractorResolver: ResolveFn<
  ContractorFormResolverData & { uuid: string }
> = () => {
  const router = inject(Router);
  const cacheService = inject(CacheService);
  const dataProvidersService = inject(DataProvidersService);
  const fb = inject(NonNullableFormBuilder);
  const preferencesService = inject(PreferencesService);
  const toastService = inject(OnyxToastService);

  const state = router.currentNavigation()?.extras?.state;
  const country = state?.[ContractorsRouterStateKey.COUNTRY];
  const vatId = state?.[ContractorsRouterStateKey.VAT_ID];

  const cacheKey = `${ContractorsStorageKey.ADD_CONTRACTOR_FORM}-${country}-${vatId}`;
  const cache = cacheService.get<PartialDeep<ContractorForm>>(cacheKey, {
    keepOnNavigation: true,
  });

  const form = ContractorFormHelper.buildForm(fb, preferencesService);
  const baseForm = form.getRawValue();

  const getInitialData = (
    companyData: CompanyData | null,
    uuid: string,
  ): PartialDeep<ContractorForm> => {
    return {
      ...baseForm,
      companyProfile: {
        ...baseForm.companyProfile,
        country,
        vatId,
        companyName: companyData?.companyName,
        displayName: companyData?.companyName?.slice(0, 48),
      },
      addresses: {
        ...baseForm.addresses,
        mainAddress: companyData?.mainAddress,
      },
      invoiceDocuments: [
        ContractorInvoiceDocumentsFormComponent.buildInvoiceDocumentsForm(
          fb,
          uuid,
        ).getRawValue(),
      ],
      invoices: [
        ContractorInvoiceFormComponent.buildForm(fb, uuid).getRawValue(),
      ],
    };
  };

  return dataProvidersService.getCompanyData(country, vatId).pipe(
    catchError(() => {
      toastService.showInformation({
        message: 'contractors.toasts.contractDataNotFoundInRegisters',
        keepOnNavigation: true,
      });
      return of(null);
    }),
    map((companyData) => {
      const uuid = baseForm.uuid;
      const restoredCache = cache
        ? {
            ...cache,
            uuid,
            invoiceDocuments: cache.invoiceDocuments?.map((document) => ({
              ...document,
              branchUuid:
                document.branchUuid === cache.uuid ? uuid : document.branchUuid,
            })),
            invoices: cache.invoices?.map((invoice) => ({
              ...invoice,
              branchUuid:
                invoice.branchUuid === cache.uuid ? uuid : invoice.branchUuid,
            })),
          }
        : null;

      return {
        form: restoredCache ?? getInitialData(companyData, uuid),
        initial: getInitialData(companyData, uuid),
        companyData,
        uuid,
      };
    }),
  );
};
